import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, LogOut, Building2, FlaskConical, DollarSign, Ban, RotateCcw, Plus, Users, AlertTriangle, Store, X, Settings2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-700",
  trial: "bg-blue-100 text-blue-700",
  teste: "bg-blue-100 text-blue-700",
  pending: "bg-amber-100 text-amber-700",
  suspended: "bg-red-100 text-red-700",
  teste_expirado: "bg-slate-200 text-slate-500",
};
const STATUS_LABEL = {
  active: "Ativa", trial: "Teste", teste: "Teste", pending: "Pendente",
  suspended: "Suspensa", teste_expirado: "Teste expirado",
};

const fmtData = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "-");

export default function AdminPanel() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);

  const [aba, setAba] = useState("contas");
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [testes, setTestes] = useState([]);
  const [filtro, setFiltro] = useState("todas");
  const [novaOpen, setNovaOpen] = useState(false);
  const [nova, setNova] = useState({ nome: "", email: "", empresa: "", cpf_cnpj: "", telefone: "" });
  const [credOpen, setCredOpen] = useState(null);
  const [lojasOpen, setLojasOpen] = useState(null); // tenant selecionado para gerir lojas
  const [lojasTenant, setLojasTenant] = useState([]);
  const [novaLojaTenant, setNovaLojaTenant] = useState("");
  const [featsOpen, setFeatsOpen] = useState(null); // tenant selecionado para gerir recursos
  const [featsData, setFeatsData] = useState({ features: {}, labels: {}, defaults: {} });

  const http = useCallback(() => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }), [token]);

  const carregar = useCallback(async () => {
    try {
      const h = http();
      const [s, t, te] = await Promise.all([
        h.get("/admin/stats"), h.get("/admin/tenants"), h.get("/admin/test-accounts"),
      ]);
      setStats(s.data); setTenants(t.data.tenants || []); setTestes(te.data.test_accounts || []);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("admin_token"); setToken("");
      }
    }
  }, [http]);

  useEffect(() => { if (token) carregar(); }, [token, carregar]);

  const tenantsFiltrados = useMemo(
    () => tenants.filter((t) => filtro === "todas" || t.status_label === filtro),
    [tenants, filtro],
  );

  const login = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await axios.post(`${API}/admin/login`, { email, senha });
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Acesso administrativo concedido");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Falha no login administrativo");
    } finally { setLoading(false); }
  };

  const sair = () => { localStorage.removeItem("admin_token"); setToken(""); window.location.href = "/"; };

  const acao = async (fn, msg) => {
    try { await fn(); toast.success(msg); carregar(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro na operação"); }
  };

  const registrarPagamento = (t) => acao(() => http().post(`/admin/tenants/${t.id}/register-payment`, { meses: 1 }), "Pagamento registrado (+30 dias)");
  const suspender = (t) => acao(() => http().post(`/admin/tenants/${t.id}/suspend`), "Conta suspensa");
  const reativar = (t) => acao(() => http().post(`/admin/tenants/${t.id}/reactivate`), "Conta reativada");

  const resetarSenha = async (t) => {
    if (!window.confirm(`Gerar uma nova senha para "${t.empresa}"? A senha atual deixará de funcionar e o responsável terá que criar uma nova no próximo acesso.`)) return;
    try {
      const res = await http().post(`/admin/tenants/${t.id}/reset-password`);
      setCredOpen({ login: res.data.login, senha: res.data.senha, reset: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao resetar senha"); }
  };

  const criarConta = async () => {
    try {
      const res = await http().post("/admin/tenants", nova);
      setNovaOpen(false);
      setCredOpen({ login: res.data.login, senha: res.data.senha });
      setNova({ nome: "", email: "", empresa: "", cpf_cnpj: "", telefone: "" });
      carregar();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar conta"); }
  };

  const abrirLojas = async (t) => {
    try {
      const r = await http().get(`/admin/tenants/${t.id}/lojas`);
      setLojasTenant(r.data.lojas || []);
      setLojasOpen(t);
      setNovaLojaTenant("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao carregar lojas"); }
  };

  const addLojaTenant = async () => {
    if (!novaLojaTenant.trim()) return;
    try {
      const r = await http().post(`/admin/tenants/${lojasOpen.id}/lojas`, { nome: novaLojaTenant.trim() });
      setLojasTenant(r.data.lojas || []);
      setNovaLojaTenant("");
      toast.success("Loja adicionada");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao adicionar loja"); }
  };

  const removerLojaTenant = async (nome) => {
    if (!window.confirm(`Remover a loja "${nome}" desta conta?`)) return;
    try {
      const r = await http().delete(`/admin/tenants/${lojasOpen.id}/lojas/${encodeURIComponent(nome)}`);
      setLojasTenant(r.data.lojas || []);
      toast.success("Loja removida");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao remover loja"); }
  };

  const abrirFeatures = async (t) => {
    try {
      const r = await http().get(`/admin/tenants/${t.id}/features`);
      setFeatsData(r.data);
      setFeatsOpen(t);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao carregar recursos"); }
  };

  const toggleFeat = (k) => {
    setFeatsData((d) => ({ ...d, features: { ...d.features, [k]: !d.features[k] } }));
  };

  const salvarFeatures = async () => {
    try {
      const r = await http().put(`/admin/tenants/${featsOpen.id}/features`, { features: featsData.features });
      setFeatsData((d) => ({ ...d, features: r.data.features }));
      toast.success("Recursos atualizados");
      setFeatsOpen(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar recursos"); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl border-0">
          <CardContent className="pt-8">
            <div className="mx-auto w-14 h-14 bg-slate-900 rounded-full flex items-center justify-center mb-4">
              <ShieldCheck className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 text-center">Acesso Administrativo</h1>
            <p className="text-sm text-slate-500 text-center mt-1 mb-6">Painel de gestão de contas</p>
            <form onSubmit={login} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-700">E-mail</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="admin-login-email" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Senha</Label>
                <Input type="password" value={senha} onChange={(e) => setSenha(e.target.value)} className="h-11" data-testid="admin-login-senha" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white" data-testid="admin-login-submit">
                {loading ? "Entrando..." : "Entrar"}
              </Button>
              <a href="/" className="block text-center text-sm text-slate-400 hover:text-slate-600 mt-2">Voltar ao sistema</a>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] overflow-y-auto bg-slate-50" data-testid="admin-panel">
      <header className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" />
          <h1 className="text-lg font-bold">Painel Administrativo</h1>
        </div>
        <Button variant="ghost" className="text-white hover:bg-white/10" onClick={sair} data-testid="admin-logout">
          <LogOut className="w-4 h-4 mr-2" /> Sair
        </Button>
      </header>

      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
            <StatCard icon={Building2} label="Contas totais" value={stats.total} />
            <StatCard icon={FlaskConical} label="Testes ativos" value={stats.testes_ativos} />
            <StatCard icon={AlertTriangle} label="Vencendo (5 dias)" value={stats.vencendo_5_dias} tone="amber" />
            <StatCard icon={DollarSign} label="Pagamentos" value={stats.pagamentos} tone="emerald" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2">
          <TabBtn active={aba === "contas"} onClick={() => setAba("contas")} testid="admin-tab-contas"><Building2 className="w-4 h-4 mr-1.5" />Contas</TabBtn>
          <TabBtn active={aba === "testes"} onClick={() => setAba("testes")} testid="admin-tab-testes"><FlaskConical className="w-4 h-4 mr-1.5" />Contas de Teste</TabBtn>
        </div>

        {aba === "contas" && (
          <Card className="bg-white border-slate-200">
            <CardContent className="pt-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  {["todas", "active", "trial", "pending", "suspended"].map((s) => (
                    <button key={s} onClick={() => setFiltro(s)} data-testid={`admin-filtro-${s}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filtro === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                      {s === "todas" ? "Todas" : (STATUS_LABEL[s] || s)}
                    </button>
                  ))}
                </div>
                <Button onClick={() => setNovaOpen(true)} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="admin-nova-conta">
                  <Plus className="w-4 h-4 mr-2" /> Nova conta
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="py-2 pr-3">Empresa</th><th className="pr-3">Responsável</th><th className="pr-3">E-mail</th>
                      <th className="pr-3">Status</th><th className="pr-3">Vencimento</th><th className="pr-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantsFiltrados.map((t) => (
                      <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`admin-tenant-${t.id}`}>
                        <td className="py-2.5 pr-3 font-medium text-slate-800">{t.empresa}{t.legacy ? " ⭐" : ""}</td>
                        <td className="pr-3 text-slate-600">{t.nome}</td>
                        <td className="pr-3 text-slate-600">{t.email}</td>
                        <td className="pr-3"><Badge className={`${STATUS_STYLE[t.status_label] || "bg-slate-100"} border-0`}>{STATUS_LABEL[t.status_label] || t.status_label}</Badge></td>
                        <td className="pr-3 text-slate-600">{fmtData(t.due_date)}</td>
                        <td className="pr-3">
                          {!t.legacy && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-8 text-slate-700 border-slate-200" onClick={() => abrirLojas(t)} data-testid={`admin-lojas-${t.id}`} title="Gerir lojas"><Store className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="outline" className="h-8 text-indigo-600 border-indigo-200" onClick={() => abrirFeatures(t)} data-testid={`admin-feats-${t.id}`} title="Recursos / abas liberadas"><Settings2 className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="outline" className="h-8 text-emerald-600 border-emerald-200" onClick={() => registrarPagamento(t)} data-testid={`admin-pagar-${t.id}`} title="Registrar pagamento (+30 dias)"><DollarSign className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="outline" className="h-8 text-amber-600 border-amber-200" onClick={() => resetarSenha(t)} data-testid={`admin-reset-senha-${t.id}`} title="Gerar nova senha (esqueceu a senha)"><KeyRound className="w-3.5 h-3.5" /></Button>
                              {t.status === "suspended" ? (
                                <Button size="sm" variant="outline" className="h-8 text-blue-600 border-blue-200" onClick={() => reativar(t)} data-testid={`admin-reativar-${t.id}`} title="Reativar"><RotateCcw className="w-3.5 h-3.5" /></Button>
                              ) : (
                                <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200" onClick={() => suspender(t)} data-testid={`admin-suspender-${t.id}`} title="Suspender"><Ban className="w-3.5 h-3.5" /></Button>
                              )}
                            </div>
                          )}
                          {t.legacy && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-8 text-slate-700 border-slate-200" onClick={() => abrirLojas(t)} data-testid={`admin-lojas-${t.id}`} title="Gerir lojas"><Store className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="outline" className="h-8 text-indigo-600 border-indigo-200" onClick={() => abrirFeatures(t)} data-testid={`admin-feats-${t.id}`} title="Recursos / abas liberadas"><Settings2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {tenants.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-8">Nenhuma conta</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {aba === "testes" && (
          <Card className="bg-white border-slate-200">
            <CardContent className="pt-5">
              <p className="text-base font-semibold text-slate-800 flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-slate-500" /> Contas de teste criadas — dados para contato</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="py-2 pr-3">Nome</th><th className="pr-3">E-mail / Login</th><th className="pr-3">Telefone</th>
                      <th className="pr-3">CPF</th><th className="pr-3">Criada</th><th className="pr-3">Expira</th><th className="pr-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testes.map((t) => (
                      <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`admin-teste-${t.id}`}>
                        <td className="py-2.5 pr-3 font-medium text-slate-800">{t.nome}</td>
                        <td className="pr-3 text-slate-600">{t.email}</td>
                        <td className="pr-3 text-slate-600">{t.telefone || "-"}</td>
                        <td className="pr-3 text-slate-600">{t.cpf}</td>
                        <td className="pr-3 text-slate-600">{fmtData(t.created_at)}</td>
                        <td className="pr-3 text-slate-600">{fmtData(t.expires_at)}</td>
                        <td className="pr-3"><Badge className={`${t.ativo ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"} border-0`}>{t.ativo ? "Ativa" : "Expirada"}</Badge></td>
                      </tr>
                    ))}
                    {testes.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">Nenhuma conta de teste</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Nova conta */}
      <Dialog open={novaOpen} onOpenChange={setNovaOpen}>
        <DialogContent className="bg-white" data-testid="admin-nova-conta-modal">
          <DialogHeader><DialogTitle>Nova conta</DialogTitle><DialogDescription>Cadastro manual de uma nova ótica.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {[["nome", "Nome do responsável"], ["empresa", "Empresa"], ["email", "E-mail (login)"], ["cpf_cnpj", "CPF/CNPJ"], ["telefone", "Telefone"]].map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-slate-600 text-sm">{label}</Label>
                <Input value={nova[k]} onChange={(e) => setNova((n) => ({ ...n, [k]: e.target.value }))} data-testid={`admin-nova-${k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovaOpen(false)}>Cancelar</Button>
            <Button className="bg-slate-900 text-white" onClick={criarConta} data-testid="admin-nova-salvar">Criar conta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credenciais geradas */}
      <Dialog open={!!credOpen} onOpenChange={(o) => !o && setCredOpen(null)}>
        <DialogContent className="bg-white sm:max-w-sm" data-testid="admin-cred-modal">
          <DialogHeader><DialogTitle>{credOpen?.reset ? "Nova senha gerada 🔑" : "Conta criada ✅"}</DialogTitle><DialogDescription>Anote e envie as credenciais ao cliente. A senha deve ser trocada no 1º acesso.</DialogDescription></DialogHeader>
          <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-2">
            <div><span className="text-slate-500">Login:</span> <strong>{credOpen?.login}</strong></div>
            <div><span className="text-slate-500">Senha:</span> <strong>{credOpen?.senha}</strong></div>
          </div>
          <DialogFooter><Button className="bg-slate-900 text-white" onClick={() => setCredOpen(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gerir recursos (features / abas) do tenant */}
      <Dialog open={!!featsOpen} onOpenChange={(o) => !o && setFeatsOpen(null)}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="admin-feats-modal">
          <DialogHeader>
            <DialogTitle>Recursos — {featsOpen?.empresa}</DialogTitle>
            <DialogDescription>Ative ou desative abas e recursos para esta conta. Mudanças refletem no menu lateral do lojista.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {Object.keys(featsData.labels || {}).map((k) => (
              <label key={k} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`admin-feat-row-${k}`}>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{featsData.labels[k]}</p>
                  <p className="text-[11px] text-slate-400">{k === "provador_virtual" ? "Consome créditos por uso no catálogo público" : `/${k}`}</p>
                </div>
                <Switch
                  checked={!!featsData.features[k]}
                  onCheckedChange={() => toggleFeat(k)}
                  data-testid={`admin-feat-toggle-${k}`}
                  className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-300"
                />
              </label>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFeatsOpen(null)}>Cancelar</Button>
            <Button className="bg-slate-900 text-white" onClick={salvarFeatures} data-testid="admin-feats-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gerir lojas do tenant */}
      <Dialog open={!!lojasOpen} onOpenChange={(o) => !o && setLojasOpen(null)}>        <DialogContent className="bg-white sm:max-w-md" data-testid="admin-lojas-modal">
          <DialogHeader>
            <DialogTitle>Lojas — {lojasOpen?.empresa}</DialogTitle>
            <DialogDescription>Gerencie as lojas desta conta. A conta deve ter pelo menos uma loja.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {lojasTenant.length === 0 && <p className="text-sm text-slate-400">Nenhuma loja encontrada.</p>}
            {lojasTenant.map((l) => (
              <div key={l} className="flex items-center justify-between rounded-lg border border-slate-200 p-2" data-testid={`admin-loja-row-${l}`}>
                <span className="text-sm text-slate-700">{l}</span>
                <Button size="icon" variant="ghost" onClick={() => removerLojaTenant(l)} disabled={lojasTenant.length <= 1} data-testid={`admin-loja-del-${l}`}>
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Input value={novaLojaTenant} onChange={(e) => setNovaLojaTenant(e.target.value)} placeholder="Nome da nova loja" data-testid="admin-nova-loja-input" />
            <Button onClick={addLojaTenant} className="bg-slate-900 text-white" data-testid="admin-nova-loja-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLojasOpen(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <Card className="bg-white border-slate-200">
    <CardContent className="pt-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
        </div>
        <Icon className={`w-8 h-8 ${tone === "amber" ? "text-amber-500" : tone === "emerald" ? "text-emerald-500" : "text-slate-300"}`} />
      </div>
    </CardContent>
  </Card>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center transition-colors ${active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
    {children}
  </button>
);
